/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useRef } from 'react';
import '../../styles/CometChatLogin/CometChatLogin.css';
import { CometChatUIKit } from '@cometchat/chat-uikit-react';
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { dlog } from "../../utils/debugLog";
import {
  isInIframe,
  isStorageWritable,
  requestStorageAccessIfNeeded,
  withTimeout,
} from "../../utils/iframeEnv";

/**
 * Brand logo shown on the connecting / error screens. Sourced from the
 * storefront's CDN so we don't have to ship a duplicate copy in this
 * repo. Same image we use for the (now-disabled) docked widget icon
 * in CometChatSettings.noCode.styles.openIcon.
 */
const BRAND_LOGO_URL =
  "https://cdn.shopify.com/s/files/1/0677/2647/5492/files/Redan_Chequy_1_sq.webp?v=1770941679";

const AUTH_KEY = process.env.REACT_APP_COMETCHAT_AUTH_KEY || "";
const LOCK_KEY = "cometchat_login_in_progress";

// How long any single SDK call is allowed to hang before we surface an
// error. Picked to be longer than a reasonable cold start over a slow
// mobile connection but short enough that the user isn't staring at a
// spinner forever.
const LOGIN_STEP_TIMEOUT_MS = 20000;

// How long a lock written by *another* page load is considered fresh
// enough to be a genuine concurrent login (vs. a stale leftover). Most
// real logins finish in well under 5s; anything older than this came
// from a previous page that abandoned its work.
const LOCK_FRESHNESS_MS = 5000;

// How long the polling loop will wait for a concurrent lock holder to
// finish before giving up and trying its own login. This bounds the
// "another iframe holds the lock" path so a dead lock-holder can't
// leave us spinning forever.
const POLL_GIVEUP_MS = 8000;

/**
 * Per-page-load identifier baked into our lock value. If we read a lock
 * back and the sessionId doesn't match this one, we know the lock came
 * from a *different* page load (e.g., the user navigated away while a
 * login was in flight, then came back). That lock is treated as stale
 * past LOCK_FRESHNESS_MS and our acquireLock() will overwrite it.
 *
 * Generated once per module evaluation, so every page load gets a fresh
 * value.
 */
const SESSION_ID = (() => {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof (crypto as Crypto & { randomUUID?: () => string }).randomUUID ===
        "function"
    ) {
      return (crypto as Crypto & { randomUUID: () => string }).randomUUID();
    }
  } catch {
    /* ignore */
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
})();

interface LockValue {
  sessionId: string;
  lockedUid: string;
  timestamp: number;
}

function isInvalidUid(uid: string | null): boolean {
  if (!uid || uid.trim() === "") return true;
  if (uid.includes("{{") || uid.includes("}}")) return true;
  return false;
}

/**
 * Extract a usable display name from the URL `name` param.
 *
 * Why this exists: many Shopify customers register with email only, so
 * `customer.first_name` / `customer.last_name` are blank in Liquid. The
 * URL ends up with `&name=` (empty) or `&name=%20` (single space). The
 * old `params.get("name") || "..."` fallback only caught the empty case
 * — a single-space name would slip through and become the literal
 * display name in CometChat's dashboard. Trim first, then fall back.
 */
function readUserName(params: URLSearchParams): string {
  const raw = (params.get("name") ?? "").trim();
  return raw.length > 0 ? raw : "Community Member";
}

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore — partitioned iframe storage will throw here */
  }
}
function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function readLock(): LockValue | null {
  const raw = safeGetItem(LOCK_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.sessionId === "string" &&
      typeof parsed.lockedUid === "string" &&
      typeof parsed.timestamp === "number"
    ) {
      return parsed as LockValue;
    }
  } catch {
    /* corrupt — fall through */
  }
  return null;
}

/**
 * Try to claim the login lock for this page load.
 * Returns:
 *   - true  → we own the lock and should drive the login.
 *   - false → another *active* page load owns the lock; we should poll.
 *
 * Stale locks (different session, older than LOCK_FRESHNESS_MS) are
 * silently overwritten — that's the case the previous version got wrong,
 * causing fresh page loads to wait on dead lock-holders forever.
 */
function acquireLock(uid: string): boolean {
  const existing = readLock();
  if (existing) {
    // If this page load already wrote it, just refresh.
    if (existing.sessionId === SESSION_ID) {
      safeSetItem(
        LOCK_KEY,
        JSON.stringify({
          sessionId: SESSION_ID,
          lockedUid: uid,
          timestamp: Date.now(),
        }),
      );
      return true;
    }
    const age = Date.now() - existing.timestamp;
    if (existing.lockedUid === uid && age < LOCK_FRESHNESS_MS) {
      dlog.log(
        `Active login lock detected (age=${age}ms, sessionId=${existing.sessionId.slice(
          0,
          6,
        )}). Will poll instead.`,
      );
      return false;
    }
    dlog.warn(
      `Stale lock from previous page load (age=${age}ms). Overwriting.`,
    );
  }
  safeSetItem(
    LOCK_KEY,
    JSON.stringify({
      sessionId: SESSION_ID,
      lockedUid: uid,
      timestamp: Date.now(),
    }),
  );
  return true;
}

function releaseLock() {
  // Only release if we own it. This avoids a stale-lock-from-elsewhere
  // race where we'd happily delete someone else's fresh lock.
  const existing = readLock();
  if (!existing || existing.sessionId === SESSION_ID) {
    safeRemoveItem(LOCK_KEY);
  }
}

/**
 * Tell App.tsx to re-fetch the logged-in user. Used after the polling
 * path detects a session that was established by a sibling page load —
 * in that case the SDK's loginSuccess listener never fires for *us*,
 * so App.tsx would otherwise stay on the login screen forever.
 */
function dispatchSessionDetected(): void {
  try {
    window.dispatchEvent(new Event("cometchat-session-detected"));
  } catch {
    /* ignore */
  }
}

const CometChatLogin = () => {
  const [error, setError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(true);
  const [statusText, setStatusText] = useState("Connecting to secure chat...");
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlUid = params.get("uid");

    dlog.info("CometChatLogin mounted.", {
      hasUid: !!urlUid,
      inIframe: isInIframe(),
      storageWritable: isStorageWritable(),
      sessionId: SESSION_ID.slice(0, 8),
    });

    if (isInvalidUid(urlUid)) {
      setIsLoggingIn(false);
      setError("Please log into the store to access the community chat.");
      return;
    }

    const uid = urlUid!;
    const userName = readUserName(params);

    if (!acquireLock(uid)) {
      // Another *fresh* page load is mid-login for the same UID. Poll
      // briefly for completion. If it doesn't finish within
      // POLL_GIVEUP_MS we assume the lock-holder died and steal the lock.
      dlog.log("Polling for sibling login completion.");
      setStatusText("Joining existing session...");
      const pollStart = Date.now();
      let cancelled = false;

      const poll = setInterval(() => {
        if (cancelled) return;
        CometChat.getLoggedinUser()
          .then((user) => {
            if (cancelled) return;
            if (user && user.getUid() === uid) {
              clearInterval(poll);
              dlog.info(
                "Sibling login finished; session detected. Notifying App.",
              );
              // Critical: tell App.tsx to refresh — the SDK's loginSuccess
              // event already fired for the sibling, so it won't fire for
              // us, and App would otherwise stay stuck on the login UI.
              dispatchSessionDetected();
            } else if (Date.now() - pollStart > POLL_GIVEUP_MS) {
              clearInterval(poll);
              dlog.warn(
                "Sibling login didn't finish in time. Stealing lock and trying ourselves.",
              );
              safeRemoveItem(LOCK_KEY);
              if (acquireLock(uid)) {
                setStatusText("Connecting to secure chat...");
                handleAutoLogin(uid, userName);
              }
            }
          })
          .catch(() => {});
      }, 500);
      return () => {
        cancelled = true;
        clearInterval(poll);
      };
    }

    handleAutoLogin(uid, userName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function updateUserName(name: string) {
    try {
      const user = await CometChat.getLoggedinUser();
      if (!user) return;
      const updatedUser = new CometChat.User(user.getUid());
      updatedUser.setName(name);
      await CometChat.updateCurrentUserDetails(updatedUser);
      dlog.info(`User display name set to "${name}".`);
    } catch (err) {
      dlog.warn("Could not update user name:", err);
    }
  }

  async function handleAutoLogin(uid: string, userName: string) {
    try {
      // Step 0: in iframes, ask the OS for first-party storage access if
      // the API is available. This is a no-op on desktop; on iOS WKWebView
      // it's the difference between the SDK persisting its auth token
      // and not.
      if (isInIframe()) {
        setStatusText("Preparing secure connection...");
        await requestStorageAccessIfNeeded();
        if (!isStorageWritable()) {
          dlog.error(
            "localStorage is not writable in this iframe. CometChat cannot persist its session token.",
          );
          throw new Error(
            "Browser is blocking storage in this iframe. Try opening the chat in your phone's browser instead.",
          );
        }
      }

      setStatusText("Checking existing session...");
      const loggedInUser = await withTimeout(
        CometChat.getLoggedinUser(),
        LOGIN_STEP_TIMEOUT_MS,
        "getLoggedinUser",
      );
      if (!isMounted.current) {
        releaseLock();
        return;
      }

      if (loggedInUser) {
        if (loggedInUser.getUid() === uid) {
          dlog.info(`Already authenticated as ${uid}.`);
          await updateUserName(userName);
          // Tell App.tsx in case it hasn't realized yet (e.g. session was
          // restored by the SDK without firing a loginSuccess event).
          dispatchSessionDetected();
          releaseLock();
          return;
        }
        dlog.log(
          `Logged in as ${loggedInUser.getUid()} but URL says ${uid}. Switching users.`,
        );
        setStatusText("Switching accounts...");
        await withTimeout(
          CometChat.logout(),
          LOGIN_STEP_TIMEOUT_MS,
          "CometChat.logout",
        );
      }

      setStatusText("Connecting to secure chat...");
      await attemptLogin(uid, userName);
    } catch (err: any) {
      releaseLock();
      if (!isMounted.current) return;
      dlog.error("handleAutoLogin failed:", err);
      setError(humanizeError(err));
      setIsLoggingIn(false);
    }
  }

  async function attemptLogin(uid: string, userName: string) {
    try {
      dlog.log(`Attempting CometChatUIKit.login for uid=${uid}`);
      await withTimeout(
        CometChatUIKit.login(uid),
        LOGIN_STEP_TIMEOUT_MS,
        "CometChatUIKit.login",
      );
      if (!isMounted.current) {
        releaseLock();
        return;
      }
      dlog.info("Login successful.");
      await updateUserName(userName);
      releaseLock();
    } catch (loginErr: any) {
      if (!isMounted.current) {
        releaseLock();
        return;
      }
      const isNotFound =
        loginErr?.code === "ERR_UID_NOT_FOUND" ||
        loginErr?.message?.toLowerCase?.().includes("not found");

      if (isNotFound) {
        dlog.log("UID not found, registering new user...");
        await registerAndLogin(uid, userName);
      } else {
        releaseLock();
        throw loginErr;
      }
    }
  }

  async function registerAndLogin(uid: string, name: string) {
    try {
      setStatusText("Creating chat profile...");
      dlog.log(`Creating CometChat profile for uid=${uid}`);
      const user = new CometChat.User(uid);
      user.setName(name);
      await withTimeout(
        CometChat.createUser(user, AUTH_KEY),
        LOGIN_STEP_TIMEOUT_MS,
        "CometChat.createUser",
      );
      setStatusText("Connecting to secure chat...");
      await withTimeout(
        CometChatUIKit.login(uid),
        LOGIN_STEP_TIMEOUT_MS,
        "CometChatUIKit.login (post-register)",
      );
      if (!isMounted.current) {
        releaseLock();
        return;
      }
      dlog.info("Post-registration login successful.");
      releaseLock();
    } catch (err: any) {
      if (!isMounted.current) {
        releaseLock();
        return;
      }

      // Race: a parallel iframe may have created the user between our
      // 404 and our createUser call. If so, just log in.
      if (err?.code === "ERR_UID_ALREADY_EXISTS") {
        dlog.log(
          "User already exists (created by parallel session). Logging in...",
        );
        try {
          await withTimeout(
            CometChatUIKit.login(uid),
            LOGIN_STEP_TIMEOUT_MS,
            "CometChatUIKit.login (after exists)",
          );
          if (!isMounted.current) {
            releaseLock();
            return;
          }
          dlog.info("Login successful after ERR_UID_ALREADY_EXISTS.");
          releaseLock();
          return;
        } catch (retryErr: any) {
          releaseLock();
          dlog.error("Login failed after ERR_UID_ALREADY_EXISTS:", retryErr);
          setError(humanizeError(retryErr));
          setIsLoggingIn(false);
          return;
        }
      }

      releaseLock();
      dlog.error("Registration/login failed:", err);
      setError(humanizeError(err));
      setIsLoggingIn(false);
    }
  }

  /**
   * Map low-level errors to messages a Shopify customer can act on.
   */
  function humanizeError(err: any): string {
    const msg = err?.message || "";
    if (/Timed out/i.test(msg)) {
      return "The chat is taking longer than expected to connect. Please check your connection and tap Retry.";
    }
    if (/blocking storage/i.test(msg)) {
      return "Your browser is blocking secure storage for this chat. Try opening the page in your phone's browser, or enable cross-site cookies.";
    }
    if (/network|failed to fetch/i.test(msg)) {
      return "Could not reach the chat server. Please check your connection and try again.";
    }
    return `Chat error: ${msg || "Unknown error"}`;
  }

  return (
    <div
      className="cometchat-login__container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        textAlign: 'center',
        padding: '20px',
      }}
    >
      <div className="cometchat-login__logo" style={{ marginBottom: '24px' }}>
        <img
          src={BRAND_LOGO_URL}
          alt="Redan"
          style={{ width: '120px', height: 'auto' }}
        />
      </div>

      {isLoggingIn && !error && (
        <div className="login-loader">
          <p style={{ color: '#666', fontSize: '14px' }}>{statusText}</p>
          <div
            className="cometchat-login__user-selection-indicator"
            style={{ display: 'block', margin: '10px auto' }}
          >
            <div className="cometchat-login__user-selection-checked" />
          </div>
        </div>
      )}

      {error && (
        <div style={{ color: '#ff4d4d', maxWidth: '320px' }}>
          <p>{error}</p>
          <button
            onClick={() => {
              releaseLock();
              setError(null);
              setIsLoggingIn(true);
              setStatusText("Connecting to secure chat...");
              const params = new URLSearchParams(window.location.search);
              const uid = params.get("uid");
              if (!isInvalidUid(uid)) {
                acquireLock(uid!);
                handleAutoLogin(uid!, readUserName(params));
              }
            }}
            style={{
              marginTop: '15px',
              padding: '8px 16px',
              backgroundColor: '#005fff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Retry Connection
          </button>
        </div>
      )}
    </div>
  );
};

export default CometChatLogin;
